<template>
	<v-card tile class="border-right border-radius-bottom-right border-radius0 pa-1" elevation="0" height="100%">
		<v-container>
			<v-btn
				v-if="edit"
				:disabled="detailedUser.userData.status === 99"
				:ripple="false"
				class="text-center"
				color="white"
				block
				@click="synchronizeDrawers('patientEdit')">
				<v-icon left small color="main_black">mdi-pencil</v-icon>
				<span class="color-black font-size13">{{ $t('edit-details') }}</span>
			</v-btn>
		</v-container>
		<v-container>
			<v-row>
				<v-col>
					<span class="font-weight-bold color-black font-size18 float-left">
						{{ detailedUser.userData.firstName + ' ' + detailedUser.userData.lastName }}
					</span>
				</v-col>
				<v-col>
					<span class="text-uppercase float-right mt-1 font-size14">
						<v-icon
							small
							class="mr-1 font-size14"
							:color="getUserColorStatus(detailedUser.userData.status)"
							style="margin-top: -2px">
							mdi-checkbox-blank-circle
						</v-icon>
						<span :style="{ color: getUserColorStatus(detailedUser.userData.status) }" style="font-weight: bold">
							{{ getUserStatus(detailedUser.userData.status) }}
						</span>
					</span>
				</v-col>
			</v-row>
			<v-row dense>
				<v-col class="text-left">
					<v-icon size="15" color="primary">mdi-email</v-icon>
					<span class="color-primary font-size15"> {{ detailedUser.userData.email }}</span>
				</v-col>
				<v-col v-if="detailedUser.userData.status !== 99" class="text-right">
					<v-btn v-if="edit" class="color-primary" x-small @click.stop="openDeceasedDialog">
						<v-icon x-small>mdi-grave-stone</v-icon>
						{{ $t('mark-deceased') }}
					</v-btn>
				</v-col>

				<v-overlay :value="loading">
					<v-progress-circular indeterminate color="primary" />
				</v-overlay>
			</v-row>
			<v-row no-gutters>
				<v-col class="text-left">
					<v-icon size="15" color="primary">mdi-phone</v-icon>
					<span class="ml-2 color-primary font-size15">
						{{ phone(detailedUser.userData.phone ?? '') }}
					</span>
				</v-col>
				<v-col class="text-right">
					<v-tooltip color="primary" top>
						<template v-slot:activator="{ on }">
							<v-icon class="mr-1 color-primary font-size" @click="openDialog" v-on="on">mdi-eye</v-icon>
						</template>
						<span>{{ $t('download-medical-notes') }}</span>
					</v-tooltip>

					<v-tooltip color="primary" top>
						<template v-slot:activator="{ on }">
							<v-icon v-on="on" class="color-primary" @click="openMedicalRecords" :disabled="!getSurveyFlag">
								mdi-cloud-download
							</v-icon>
						</template>
						<span>{{ $t('download-last-month-measurements') }}</span>
					</v-tooltip>
				</v-col>
			</v-row>
			<v-row no-gutters>
				<v-col class="d-flex justify-center align-center" cols="auto">
					<span class="pr-2">
						<span class="font-weight-medium font-size12-5">RPM: </span>
						<v-icon small color="primary">
							{{ detailedUser.userData.isRpmEnabled ? 'mdi-checkbox-blank' : 'mdi-checkbox-blank-outline' }}
						</v-icon>
					</span>
					<span class="pr-2">
						<span class="font-weight-medium font-size12-5">RTM: </span>
						<v-icon small color="primary">
							{{ detailedUser.userData.isRtmEnabled ? 'mdi-checkbox-blank' : 'mdi-checkbox-blank-outline' }}
						</v-icon>
					</span>
					<span>
						<span class="font-weight-medium font-size12-5">CCM: </span>
						<v-icon small color="primary">
							{{ detailedUser.userData.isCcmEnabled ? 'mdi-checkbox-blank' : 'mdi-checkbox-blank-outline' }}
						</v-icon>
					</span>
				</v-col>
				<v-spacer />
				<v-col cols="auto" class="text-right d-flex align-center">
					<v-tooltip color="primary" top>
						<template #activator="{ on }">
							<img
								v-on="on"
								class="mr-1 cursor-pointer"
								height="24"
								@click.stop="consultationDialog = true"
								:src="endoscope" />
						</template>
						{{ $t('video-consultation') }}
					</v-tooltip>

					<v-tooltip color="primary" top>
						<template #activator="{ on }">
							<v-icon
								class="mr-1 color-primary font-size"
								v-on="on"
								color="primary"
								@click.stop="stethoscopeDialog = true">
								mdi-stethoscope
							</v-icon>
						</template>
						{{ $t('stethoscope-streaming') }}
					</v-tooltip>

					<v-tooltip color="primary" top>
						<template v-slot:activator="{ on }">
							<v-icon
								class="mr-1 color-primary font-size"
								v-on="on"
								:disabled="detailedUser.userData.status === 99"
								v-if="!video"
								@click.stop="initiateCall(false)">
								mdi-phone
							</v-icon>
						</template>
						<span>{{ $t('voice-call-patient') }}</span>
					</v-tooltip>

					<v-tooltip color="primary" top>
						<template v-slot:activator="{ on }">
							<v-icon
								v-on="on"
								class="color-primary"
								:disabled="detailedUser.userData.status === 99"
								v-if="!video"
								@click.stop="initiateCall(true)">
								mdi-video
							</v-icon>
						</template>
						<span>{{ $t('video-call-patient') }}</span>
					</v-tooltip>
				</v-col>
			</v-row>
			<v-row class="pa-0 ma-0 mt-2">
				<v-col cols="4" class="data-table-outline border-radius-left5 d-flex flex-column-reverse pa-0">
					<span class="font-size14">{{ $t('age') }}</span>
					<span class="font-weight-bold font-size14">{{ calculateAge(detailedUser.userData.birthdate) }}</span>
				</v-col>
				<v-col cols="4" class="data-table-outline flex-column d-flex pa-0">
					<span class="font-weight-bold font-size14 mt-1">
						{{
							userSettings.Units === 'METRIC'
								? !detailedUser.userData.patientMetadata.weightInKg
									? 'kg'
									: detailedUser.userData.patientMetadata.weightInKg + ' kg'
								: !detailedUser.userData.patientMetadata.weightInKg
								? 'lbs'
								: toLbs(detailedUser.userData.patientMetadata.weightInKg) + ' lbs'
						}}
					</span>
					<span class="font-size14">{{ $t('weight') }}</span>
				</v-col>
				<v-col cols="4" class="data-table-outline border-radius-right5 d-flex flex-column-reverse pa-0">
					<span class="font-size14">{{ $t('height') }}</span>
					<span class="font-weight-bold font-size14">
						{{
							detailedUser.userData.patientMetadata.heightInCm
								? userSettings.Units === 'IMPERIAL'
									? formatHeightInFeet(detailedUser.userData.patientMetadata.heightInCm)
									: detailedUser.userData.patientMetadata.heightInCm + ' cm'
								: userSettings.Units === 'IMPERIAL'
								? 'ft'
								: 'cm'
						}}
					</span>
				</v-col>
			</v-row>
		</v-container>

		<v-card elevation="2" class="mx-1" height="calc(100% - 263px)" id="records-card" ref="recordsCard">
			<v-card-title class="pa-1">
				<v-tabs v-model="tab" bg-color="primary" grow>
					<v-tab value="RPM" :class="{ 'selected-tab': tab === 0, 'unselected-tab': tab !== 0 }"> RPM </v-tab>
					<v-tab
						value="CCM"
						:disabled="!detailedUser.userData.isCcmEnabled"
						:class="{ 'selected-tab': tab === 1, 'unselected-tab': tab !== 1 }">
						CCM
					</v-tab>
				</v-tabs>
			</v-card-title>
			<v-card-text class="pa-1 overflow-y-auto" style="height: calc(100% - 56px)">
				<div v-if="tab === 0">
					<v-card elevation="1" class="mx-1">
						<v-card-title class="pa-0 card-header-details">
							<v-col cols="6" class="pa-0">
								<span class="float-left font-size14 font-weight-bold ml-4">{{ $t('problem-list') }}</span>
							</v-col>
							<v-col cols="6">
								<v-icon
									v-if="edit"
									:disabled="detailedUser.userData.status === 99"
									color="primary"
									size="18"
									@click="synchronizeDrawers('diagnosisEdit')"
									class="float-right">
									mdi-pencil
								</v-icon>
							</v-col>
						</v-card-title>
						<v-card-text
							v-if="detailedUser.userData.patientDiagnosisList.length > 0"
							v-html="getLatestDiagnosis"
							class="pl-4 py-0 my-0"
							:style="{ height: edit ? 'auto' : '200px' }"
							style="overflow-y: auto; max-height: 15vh"></v-card-text>
					</v-card>
					<medication-list-component
						:height="'116px'"
						class="mx-1"
						@openMedicationEditDialog="openMedicationEditDialog"
						@openViewAllMedicationsDialog="$emit('openViewAllMedicationsDialog')" />
					<v-card class="mx-1">
						<v-card-title class="pa-0 card-header-details">
							<v-col cols="6" class="pa-0">
								<span class="float-left font-size14 font-weight-bold ml-4">{{ $t('allergies') }}</span>
							</v-col>
							<v-col cols="6">
								<v-icon
									v-if="edit"
									:disabled="detailedUser.userData.status === 99"
									@click="synchronizeDrawers('allergiesEdit')"
									class="float-right"
									color="primary"
									size="18">
									mdi-pencil
								</v-icon>
							</v-col>
						</v-card-title>
						<div>
							<v-card-text
								v-html="getAllergies"
								style="overflow-y: auto; max-height: 15vh"
								class="py-0 my-0"></v-card-text>
						</div>
					</v-card>
					<v-card class="ma-1">
						<v-card-title class="pa-0 card-header-details">
							<v-col cols="6" class="pa-0">
								<span class="float-left font-size14 font-weight-bold ml-4">{{ $t('medicalTeam') }}</span>
							</v-col>
							<v-col cols="6" class="pa-0">
								<h6 @click="triggerMedicalTeam" class="float-right mr-2 color-primary pointer font-size14">
									{{ $t('view-all') }}
								</h6>
							</v-col>
						</v-card-title>
						<v-card-text class="text-left" style="overflow-y: auto; max-height: 9vh">
							<span class="my-2 font-size14 font-weight-bold">
								{{ $t('primary-doctor') }}: {{ detailedUser.userData.medicalTeamsData[0]?.primaryDoctorFullName }}
							</span>
							<v-col v-if="showMedicalTeam" class="pa-0">
								<span
									v-for="member in detailedUser.userData.medicalTeamsData[0].membersList"
									:key="member.id"
									class="d-block">
									<span>{{ member.firstName }} {{ member.lastName }}</span>
								</span>
							</v-col>
						</v-card-text>
					</v-card>
				</div>
				<div v-if="tab === 1" v-resize="calcRecordListHeight">
					<CCMRecordsList :style="{ height: recordsListHeight }" />
				</div>
			</v-card-text>
		</v-card>

		<show-notes-dialog :dialog.sync="showDialog" :detailed-user="detailedUser" v-if="showDialog" />
		<medical-records
			:assigned-medical-devices="assignedMedicalDevices"
			:dialog.sync="medicalRecords"
			:detailed-user="detailedUser"
			v-if="medicalRecords" />
		<DeceasedDialog
			v-if="dialogDelete"
			@close-dialog="dialogDelete = false"
			:dialog.sync="dialogDelete"
			:detailed-user="detailedUser" />
		<v-dialog v-model="stethoscopeDialog" v-if="stethoscopeDialog" max-width="1150" height="90vh" scrollable>
			<v-card>
				<v-card-title class="card-header d-flex align-center justify-space-between pa-3">
					{{ $t('stethoscope-streaming') }}
					<v-icon @click="stethoscopeDialog = false" color="white">mdi-close</v-icon>
				</v-card-title>
				<v-divider />
				<v-card-text class="pa-0">
					<StethoscopeStreamingComponent />
				</v-card-text>
			</v-card>
		</v-dialog>
		<v-dialog
			v-model="consultationDialog"
			v-if="consultationDialog"
			:fullscreen="consultationFullscreen"
			max-width="1150"
			height="90vh"
			scrollable>
			<v-card>
				<v-card-title class="card-header d-flex align-center justify-space-between pa-3">
					{{ $t('video-consultation') }}
					<div class="d-flex align-center">
						<v-icon @click="consultationFullscreen = !consultationFullscreen" color="white" class="mr-2">
							{{ consultationFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}
						</v-icon>
						<v-icon @click="consultationDialog = false" color="white">mdi-close</v-icon>
					</div>
				</v-card-title>
				<v-divider />
				<v-card-text class="pa-0">
					<VideoConsultationComponent :fullscreen="consultationFullscreen" />
				</v-card-text>
			</v-card>
		</v-dialog>
	</v-card>
</template>
<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { fromTo } from '@/utils/unit-converter.js';
import { AsYouType, isValidPhoneNumber } from 'libphonenumber-js';
import { doctorBlack, patientBlack, endoscope } from '@/assets';
import showNotesDialog from '@/views/dialogs/ShowNotesDialog';
import MedicalRecords from '@/views/dialogs/MedicalRecords';
import DeceasedDialog from '@/views/dialogs/DeceasedDialog.vue';
import MedicationListComponent from './MedicationListComponent.vue';
import { calculateAge } from '@/utils/utilities';
import { permissions } from '@/utils/calls';
import CCMRecordsList from '@/views/Client/components/ChronicCareManagement/CCMRecordsList.vue';
import StethoscopeStreamingComponent from './StethoscopeStreamingComponent.vue';
import VideoConsultationComponent from './VideoConsultationComponent.vue';
export default {
	props: ['edit', 'video'],
	components: {
		showNotesDialog,
		MedicalRecords,
		DeceasedDialog,
		MedicationListComponent,
		CCMRecordsList,
		StethoscopeStreamingComponent,
		VideoConsultationComponent,
	},
	data () {
		return {
			allergies: null,
			dialogDelete: false,
			patientBlack: patientBlack,
			doctorBlack: doctorBlack,
			endoscope: endoscope,
			treatments: null,
			buttonDisalbed: true,
			showMedicalTeam: false,
			getResponse: null,
			iconDisabled: true,
			loading: false,
			showDialog: false,
			filterChange: false,
			filterChat: 'LAST7',
			enableDownload: false,
			medicalRecords: false,
			tab: null,
			tabs: {
				0: 'RPM',
				1: 'CCM',
			},
			lastStart: null,
			idleTimeout: null,
			visibilityHandler: null,
			activeTime: 0,
			idleLimitMs: 60000, // 1 min of inactivity = idle
			recordsListHeight: '364px',
			stethoscopeDialog: false,
			consultationDialog: false,
			consultationFullscreen: true,
		};
	},
	computed: {
		...mapState({
			userData: (state) => state.authentication.userData,
			notificationsWithUser: (state) => state.notifications.notificationsWithUser,
			detailedUser: (state) => state.users.detailedUser,
			hubs: (state) => state.nosessionhubs.hubs,
			surveyForPatient: (state) => state.mentalhealth.surveyPatentDetails,
		}),
		...mapGetters({
			roleName: 'authentication/getRole',
			userSettings: 'authentication/getUserSettings',
			pageHeight: 'pageHeight',
			getRpmCcmSelected: 'authentication/getRpmCcmSelected',
		}),
		getSurveyFlag: {
			get () {
				return this.$store.state.mentalhealth.getSurveyFlag;
			},
			// Define a setter even if it's not used
			set (value) {
				// You can leave it empty or log a message if needed
			},
		},
		getLatestDiagnosis () {
			const diagnoses = this.detailedUser.userData.patientDiagnosisList.map((diagnosis) => {
				return `<p class="mb-1"><b>${diagnosis.diagnosisCode}</b> ${diagnosis.diagnoseDescription}</p>`;
			});
			return `<div class="text-left px-0">${diagnoses.join('')}</div>`;
		},
		assignedMedicalDevices () {
			return this.hubs.assignedMedicalDevicesList?.map((item) => ({
				deviceDefaultValuesJson: item.deviceDefaultValuesJson,
				deviceType: item.deviceType,
				measurementsData: item.measurementsData,
				deviceName: item.deviceName,
				deviceNameEsp: item.deviceNameEsp,
			}));
		},
		getAllergies () {
			// eslint-disable-next-line
			this.allergies = this.detailedUser.userData.patientMetadata.metadataInfo;
			const metadataInfo = this.detailedUser.userData.patientMetadata.metadataInfo;
			if (metadataInfo === null) {
				return null;
			}
			const formattedMetadataInfo = `<p class="mb-1">${metadataInfo}</p>`;
			return `<div class="text-left">${formattedMetadataInfo}</div>`;
		},
	},
	async mounted () {
		if (this.detailedUser === null) {
			await this.getUserById();
		}
		this.$on('enable-icon', this.iconDisabled);
		if (this.surveyForPatient) {
			this.buttonDisalbed = true;
		}
		this.checkForStartTimer();
		if (this.tab === 1) this.calcRecordListHeight();
	},
	watch: {
		filterChat () {
			this.filterChange = true;
		},
		filterChanged (val) {
			this.filterCase = val;
			this.getMyNotifications(this.filterCase);
		},
		async tab (val) {
			const tabSelected = this.tabs[val];
			await this.$store.dispatch('authentication/setRpmCcm', tabSelected);
			this.activeTime = 0;
			this.checkForStartTimer();
		},
	},

	methods: {
		...mapActions({
			stoptrackingwithpatientV2: 'timetracking/stoptrackingwithpatientV2',
		}),
		calculateAge,
		getUserColorStatus (status) {
			switch (status) {
				case 1:
					return '#09CB09';
				case 99:
					return '#000000';
				case 0:
				case -1:
					return 'grey';
				default:
					return '';
			}
		},
		getUserStatus (status) {
			switch (status) {
				case 1:
					return this.$t('active-caps');
				case 99:
					return this.$t('deceased-caps');
				case -1:
					return this.$t('pending-caps');
				case 0:
					return this.$t('inactive-caps');
			}
		},
		async openDeceasedDialog () {
			this.dialogDelete = true;
		},
		openMedicalRecords () {
			this.medicalRecords = !this.medicalRecords;
		},
		openMedicationEditDialog (prescription) {
			this.$emit('openMedicationEditDialog', prescription);
		},
		openDialog () {
			this.loading = true;
			const body = {
				patientId: this.$route.params.id,
				hospitalId: Number(localStorage.getItem('selectedHospitalAssignedId')),
			};
			this.$store
				.dispatch('appointments/getmedicalnoteforpatientbyid', body)
				.then(() => {
					// API call succeeded
					this.showDialog = !this.showDialog;
				})
				.finally(() => {
					// Hide loading indicator whether the API call succeeds or fails
					this.loading = false;
				});
		},
		triggerMedicalTeam () {
			this.showMedicalTeam = !this.showMedicalTeam;
		},
		formatHeightInFeet () {
			if (this.userSettings.Units === 'IMPERIAL') {
				const totalInches = Math.round(this.detailedUser.userData.patientMetadata.heightInCm / 2.54);
				const feet = Math.floor(totalInches / 12);
				const remainingInches = totalInches % 12;
				const tenthsOfFoot = remainingInches / 12;
				const heightInFeet = feet + tenthsOfFoot;
				return heightInFeet.toFixed(1) + ' ft';
			} else {
				return this.detailedUser.userData.patientMetadata.heightInCm + ' cm';
			}
		},
		synchronizeDrawers (drawer) {
			this.$store.commit('SET_PATIENT_DETAILS_DRAWER', drawer);
			this.$store.commit('SET_UPDATE_DETAILS_DRAWER', {
				updateDrawer: true,
				page2: 'patientdetails',
			});
		},
		phone: function (val) {
			if (val !== undefined) {
				this.phoneErrors = !isValidPhoneNumber(val);
				return new AsYouType().input(val);
			}
		},
		async getUserById () {
			this.userLoading = true;
			await this.$store.dispatch('users/getUserById', this.$route.params.id).then(() => {
				this.$store.dispatch('patientdiagnosis/getDiagnosisListForPatient', this.$route.params.id);
				this.userLoading = false;
			});
		},
		toFeet (value) {
			return fromTo(value, 'cm', 'ft-us').toFixed(2);
		},
		toLbs (value) {
			return fromTo(value, 'kg', 'lb').toFixed(1);
		},
		async initiateCall (videotrack) {
			if (this.roleName !== 'HOSPITAL_ADMIN') {
				if (await permissions()) return;
				const type = videotrack ? 'video' : 'audio';
				const body = {
					UserId: this.detailedUser?.userData?.id,
					MessageToSend: `${this.detailedUser?.userData?.firstName} ${this.detailedUser?.userData?.lastName} has started ${type} call`,
					NeedVideoTrack: videotrack,
				};
				await this.$store.dispatch('notifications/initVideoCall', body).then(async (res) => {
					this.loading = true;
					await this.goToRoom(res.data.appointmentData, videotrack);
					this.loading = false;
				});
			}
		},
		async goToRoom (item, videotrack) {
			this.$router.push({
				name: 'VideoRoom',
				params: {
					id: `${item.videoRoomId}`,
					userId: `${item.patientId}`,
					appointmentId: item.appointmentId ? item.appointmentId : item.id,
					token: localStorage.sessionToken,
					needVideoRecording: item.needVideoRecording,
					videotrack: videotrack,
				},
			});
		},

		startTracking () {
			// Start tracking if page is visible and user is active
			if (document.visibilityState === 'visible') {
				this.resumeTimer();
			}

			// Setup visibility change tracking
			this.visibilityHandler = () => {
				if (document.visibilityState === 'hidden') {
					this.pauseTimer();
				} else {
					this.resumeTimer();
				}
			};
			document.addEventListener('visibilitychange', this.visibilityHandler);

			// Setup user activity tracking
			['mousemove', 'keydown', 'scroll'].forEach((evt) => {
				window.addEventListener(evt, this.resetIdleTimer);
			});
			this.resetIdleTimer();

			// Handle unload
			window.addEventListener('beforeunload', this.cleanup);
		},
		resumeTimer () {
			if (!this.lastStart) {
				this.lastStart = Date.now();
			}
		},
		pauseTimer () {
			if (this.lastStart) {
				this.activeTime += Date.now() - this.lastStart;
				this.lastStart = null;
			}
			clearTimeout(this.idleTimeout);
		},
		resetIdleTimer () {
			clearTimeout(this.idleTimeout);
			if (!this.lastStart) {
				this.resumeTimer();
			}
			this.idleTimeout = setTimeout(() => {
				this.pauseTimer(); // User idle
			}, this.idleLimitMs);
		},
		async cleanup () {
			this.pauseTimer();

			const totalTime = this.activeTime;
			if (this.userData.roleData.roleName === 'DOCTOR' || this.userData.roleData.roleName === 'NURSE') {
				const body = {
					hospitalId: Number(localStorage.getItem('selectedHospitalAssignedId')), // get selected hospital, if there are more assigned to the user
					patientId: this.detailedUser.userData.id,
					staffId: this.userData.id,
					visitedPageUrl: 'WEB:' + this.$route.name,
					timeSpentSeconds: Math.floor(totalTime / 1000),
				};
				await this.stoptrackingwithpatientV2(body);
			}

			// Remove tracking key
			localStorage.removeItem(this.storageKey);
			this.activeTime = 0;

			document.removeEventListener('visibilitychange', this.visibilityHandler);
			['mousemove', 'keydown', 'scroll'].forEach((evt) => {
				window.removeEventListener(evt, this.resetIdleTimer);
			});
			window.removeEventListener('beforeunload', this.cleanup);
		},
		checkForStartTimer () {
			if (this.tab === 0) {
				this.storageKey = 'tracking_RPM';

				const savedStart = localStorage.getItem(this.storageKey);
				if (!savedStart) {
					localStorage.setItem(this.storageKey, Date.now().toString());
				}

				this.startTracking();
			} else {
				this.cleanup();
			}
		},
		calcRecordListHeight () {
			this.recordsListHeight = this.$refs.recordsCard.$el.clientHeight - 64 + 'px';
		},
	},
	beforeDestroy () {
		localStorage.removeItem('resFlag');
		if (this.tab === 0) {
			this.cleanup();
		}
	},
};
</script>
<style lang="scss" scoped>
.selected-tab {
	background-color: rgba(#ff585f, 0.2) !important;
}

.unselected-tab {
	background-color: #f5f5f5 !important;
	/* light gray */
}

#records-card {
	border-radius: 4px !important;
}
</style>
